import { useState } from 'react';
import {
  CheckCircle2,
  ListTodo,
  Bot,
  ChevronDown,
  ChevronRight,
  XCircle,
  Clock3,
  Wrench,
} from 'lucide-react';
import { MarkdownContent } from '@reflex/ui/components/content/MarkdownContent';
import { Spinner } from '@reflex/ui/components/shared/Spinner';
import { Button } from '@reflex/ui/components/ui/button';
import { Badge } from '@reflex/ui/components/ui/badge';
import { formatDuration } from '@reflex/ui/lib/format';
import { cn } from '@reflex/ui/lib/utils';
import { EventDetailTooltip } from '@reflex/ui/components/stream/EventMeta';
import type { PlanBlock, TaskBlock } from '../types.js';
import { AnimatedCollapse } from './AnimatedCollapse.js';
import { rendersNothing } from './block-visibility.js';

// Plan entry text rendered as plain inline text to inherit text-xs from the list
// item and keep icon alignment stable. MarkdownContent uses text-sm which breaks
// the mt-0.5 icon calibration.
function PlanEntryText({ content, className }: { content: string; className?: string }) {
  return <span className={cn('leading-normal', className)}>{content}</span>;
}

export function PlanBlockView({ block }: { block: PlanBlock }) {
  const [open, setOpen] = useState(true);

  if (rendersNothing(block)) return null;

  const completed = block.entries.filter((e) => e.status === 'completed').length;
  const total = block.entries.length;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  const Chevron = open ? ChevronDown : ChevronRight;

  return (
    <div className="group/plan relative my-1.5 rounded-lg border border-border/20 bg-muted/10">
      <Button
        variant="ghost"
        onClick={() => setOpen((v) => !v)}
        className="h-auto w-full justify-start gap-2 rounded-lg px-3 py-2 text-xs font-normal text-muted-foreground hover:bg-transparent hover:text-foreground dark:hover:bg-transparent"
      >
        <ListTodo className="size-3.5 shrink-0" />
        <span className="font-medium text-foreground/70">Plan</span>
        <div className="mx-2 h-1 flex-1 overflow-hidden rounded-full bg-border/30">
          <div
            className="h-full rounded-full bg-status-success/40 transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
        <span className="tabular-nums text-muted-foreground/60">
          {completed}/{total}
        </span>
        <Chevron className="ml-1 size-3 shrink-0" />
      </Button>
      {block.sourceEvent && (
        <span className="pointer-events-none absolute right-10 top-2.5 opacity-0 transition-opacity group-hover/plan:pointer-events-auto group-hover/plan:opacity-100">
          <EventDetailTooltip event={block.sourceEvent} />
        </span>
      )}
      <AnimatedCollapse open={open}>
        <ul className="border-t border-border/15 px-3 py-2 space-y-1.5">
          {block.entries.map((entry, i) => (
            <li key={entry.id ?? i} className="flex items-start gap-1.5 text-xs">
              {entry.status === 'completed' ? (
                <CheckCircle2 className="mt-0.5 h-3 w-3 shrink-0 text-status-success/60" />
              ) : entry.status === 'in_progress' ? (
                <Spinner size="xs" className="mt-0.5 text-foreground/60" />
              ) : (
                <div className="mt-0.5 h-3 w-3 shrink-0 rounded-full border border-border/40" />
              )}
              <PlanEntryText
                content={entry.content}
                className={cn(
                  'min-w-0 flex-1',
                  entry.status === 'completed' &&
                    'text-muted-foreground/60 line-through decoration-muted-foreground/30',
                  entry.status === 'in_progress' && 'text-foreground/80',
                  entry.status === 'pending' && 'text-muted-foreground',
                )}
              />
              {entry.priority && (
                <span
                  className={cn(
                    'shrink-0 rounded px-1 py-0.5 text-xs font-medium bg-muted',
                    entry.priority === 'high' && 'text-foreground/70',
                    entry.priority === 'medium' && 'text-muted-foreground',
                    entry.priority === 'low' && 'text-muted-foreground/60',
                  )}
                >
                  {entry.priority}
                </span>
              )}
            </li>
          ))}
        </ul>
      </AnimatedCollapse>
    </div>
  );
}

function TaskStatusIcon({ status }: { status: TaskBlock['status'] }) {
  switch (status) {
    case 'started':
    case 'in_progress':
      return <Spinner size="sm" className="text-status-info" />;
    case 'completed':
      return <CheckCircle2 className="size-4 shrink-0 text-status-success" />;
    case 'failed':
      return <XCircle className="size-4 shrink-0 text-status-error" />;
    case 'stopped':
      return <Bot className="size-4 shrink-0 text-muted-foreground" />;
  }
}

const STATUS_LABELS: Record<TaskBlock['status'], string> = {
  started: 'Starting',
  in_progress: 'Running',
  completed: 'Completed',
  failed: 'Failed',
  stopped: 'Stopped',
};

const TASK_STATUS_STYLES: Record<TaskBlock['status'], string> = {
  started: 'border-status-info/20 bg-status-info/8 text-status-info',
  in_progress: 'border-status-info/20 bg-status-info/8 text-status-info',
  completed: 'border-status-success/20 bg-status-success/8 text-status-success',
  failed: 'border-status-error/20 bg-status-error/8 text-status-error',
  stopped: 'border-border/30 bg-muted/30 text-muted-foreground',
};

function TaskDetailLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="mb-1 text-3xs font-semibold uppercase tracking-[0.12em] text-muted-foreground/50">
      {children}
    </p>
  );
}

export function TaskBlockView({ block }: { block: TaskBlock }) {
  const isRunning = block.status === 'started' || block.status === 'in_progress';
  const hasSummary = Boolean(block.summary && block.summary !== block.description);
  const hasDetails = Boolean(
    hasSummary ||
    block.prompt ||
    block.agentPath ||
    block.model ||
    block.toolUses ||
    block.durationMs,
  );
  const [expanded, setExpanded] = useState(hasDetails && !isRunning);
  const Chevron = expanded ? ChevronDown : ChevronRight;

  return (
    <div
      className={cn(
        'group/task relative my-2 overflow-hidden rounded-xl border bg-gradient-to-br from-muted/20 via-background/20 to-background/5 shadow-sm',
        block.status === 'failed' ? 'border-status-error/20' : 'border-border/25',
      )}
      data-testid="background-agent-card"
      data-status={block.status}
    >
      <Button
        type="button"
        variant="ghost"
        disabled={!hasDetails}
        aria-label="Toggle background agent details"
        aria-expanded={hasDetails ? expanded : undefined}
        onClick={() => hasDetails && setExpanded((value) => !value)}
        className="h-auto w-full justify-start gap-3 rounded-none px-3 py-2.5 text-left font-normal hover:bg-muted/20 disabled:pointer-events-none disabled:opacity-100 dark:hover:bg-muted/20"
      >
        <span
          className={cn(
            'relative flex size-8 shrink-0 items-center justify-center rounded-lg border bg-background/70 shadow-xs',
            isRunning ? 'border-status-info/25 text-status-info' : 'border-border/30',
          )}
        >
          <Bot className="size-4" />
          {isRunning ? (
            <span className="absolute -right-0.5 -top-0.5 size-2 rounded-full bg-status-info ring-2 ring-background animate-pulse" />
          ) : null}
        </span>

        <span className="min-w-0 flex-1">
          <span className="block truncate text-xs font-medium text-foreground/85">
            {block.description || `Agent ${block.taskId}`}
          </span>
        </span>

        <Badge
          variant="outline"
          className={cn('h-5 gap-1.5 rounded-full px-2 text-3xs', TASK_STATUS_STYLES[block.status])}
        >
          <TaskStatusIcon status={block.status} />
          {STATUS_LABELS[block.status]}
        </Badge>
        {hasDetails ? <Chevron className="size-3.5 shrink-0 text-muted-foreground/50" /> : null}
      </Button>

      {isRunning ? (
        <div className="h-px overflow-hidden bg-status-info/10">
          <div className="h-full w-2/3 animate-pulse bg-gradient-to-r from-transparent via-status-info/70 to-transparent" />
        </div>
      ) : null}

      {block.sourceEvent ? (
        <span className="pointer-events-none absolute right-2 top-10 opacity-0 transition-opacity group-hover/task:pointer-events-auto group-hover/task:opacity-100">
          <EventDetailTooltip event={block.sourceEvent} />
        </span>
      ) : null}

      <AnimatedCollapse open={expanded && hasDetails}>
        <div className="space-y-3 border-t border-border/15 px-3 py-3">
          {block.prompt ? (
            <div>
              <TaskDetailLabel>Delegated task</TaskDetailLabel>
              <p className="text-xs leading-relaxed whitespace-pre-wrap text-foreground/70">
                {block.prompt}
              </p>
            </div>
          ) : null}

          {hasSummary ? (
            <div>
              <TaskDetailLabel>Result</TaskDetailLabel>
              <div className="text-xs text-foreground/75">
                <MarkdownContent content={block.summary!} size="xs" />
              </div>
            </div>
          ) : null}

          <div className="flex flex-wrap items-center gap-1.5 text-3xs text-muted-foreground/60">
            {block.model ? (
              <Badge variant="secondary" className="h-5 rounded-full px-2 text-3xs font-normal">
                {block.model}
              </Badge>
            ) : null}
            {block.toolUses != null && block.toolUses > 0 ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-muted/40 px-2 py-1">
                <Wrench className="size-3" />
                {block.toolUses} {block.toolUses === 1 ? 'tool' : 'tools'}
              </span>
            ) : null}
            {block.durationMs != null ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-muted/40 px-2 py-1">
                <Clock3 className="size-3" />
                {formatDuration(block.durationMs)}
              </span>
            ) : null}
          </div>

          <p
            className="truncate font-mono text-3xs text-muted-foreground/35"
            title={block.agentPath ?? block.taskId}
          >
            {block.agentPath ?? block.taskId}
          </p>
        </div>
      </AnimatedCollapse>
    </div>
  );
}
